import { useState, useEffect } from "react";

// DatePicker components
import dayjs from "dayjs";

// firebase functions
import { db } from "firebaseDb";
import {
  getDocs,
  query,
  collection,
  orderBy,
  addDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";

import { useNavigate, useLocation } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
// Material Kit 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Orchard Selection
import Orchard from "layouts/weedsprays/createweedspray/components/Orchard";

// Chemical Selection
import Chemicals from "layouts/weedsprays/createweedspray/components/Chemicals";

require("dayjs/locale/en-au");

function CreateWeedSpray() {
  const [loading, setLoading] = useState(false);
  const [sidenavPadding, setSideNavPadding] = useState("230");
  const [openModal, setopenModal] = useState(false);
  const [operators, setOperators] = useState([]);
  const [chemicals, setChemicals] = useState([]);
  const [sprayers, setSprayers] = useState([]);

  const date = new Date();
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear() - 2000).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const formattedDate = `${year}${month}${day}${hours}${minutes}${seconds}`;

  const navigate = useNavigate();
  const [spray, setSpray] = useState({
    archive: false,
    sprayInstructionId: formattedDate,
    sprayDate: "",
    operatorWorker: [],
    chemicals: [],
    orchards: [],
    sprayer: [],
    status: "Ready to Spray",
    PPE: [],
    sprayRate: 0,
  });

  const handleOpen = () => setopenModal(true);
  const handleClose = () => setopenModal(false);

  const modalstyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };

  const steps = [
    {
      label: "Select Orchards",
      component: <Orchard spray={spray} setSpray={setSpray} />,
    },
    {
      label: "Chemicals",
      component: (
        <Chemicals
          spray={spray}
          setSpray={setSpray}
          chemicals={chemicals}
          sprayers={sprayers}
          handlers={operators.filter((op) => op.approvedHandler === true)}
        />
      ),
    },
  ];

  function findUndefinedValues(obj, path = "") {
    if (obj === undefined) {
      console.log(`Undefined value found at path: ${path}`);
      return;
    }

    if (typeof obj === "object" && obj !== null) {
      if (Array.isArray(obj)) {
        obj.forEach((value, index) => {
          findUndefinedValues(value, `${path}[${index}]`);
        });
      } else {
        Object.entries(obj).forEach(([key, value]) => {
          findUndefinedValues(value, path ? `${path}.${key}` : key);
        });
      }
    }
  }

  const saveSpray = () => {
    findUndefinedValues(spray);

    if (
      spray.chemicals.length < 1 ||
      spray.orchards.length < 1 ||
      spray.sprayer.length < 1 ||
      spray.sprayRate < 1
    ) {
      handleOpen();
    } else {
      setLoading(true);
      const sprayRef = collection(db, "Weedsprays");

      // Add the 'created' field with the server timestamp
      addDoc(sprayRef, {
        ...spray,
        created: serverTimestamp(), // Adding the created field
      }).then(() => {
        setLoading(false);
        navigate(`/weedsprays/${spray.sprayInstructionId}`);
      });
    }
  };

  // Setting Locale
  dayjs.locale("en-au");

  // Accessing Window
  const location = useLocation();

  useEffect(() => {
    // A function that sets the padding
    function handleSidenavPadding() {
      setSideNavPadding(window.innerWidth > 1200 ? "230px" : "60px");
    }

    /**
     The event listener that's calling the padding function when resizing the window.
    */
    window.addEventListener("resize", handleSidenavPadding);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleSidenavPadding();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleSidenavPadding);
  }, [location]);

  useEffect(() => {
    // Fetch Data

    const operatorRef = collection(db, "Operators");
    const qo = query(operatorRef, orderBy("name", "asc"));
    getDocs(qo).then((querySnapshot) => {
      const tempoperators = [];
      querySnapshot.forEach((docu) => {
        if (!docu.data().archive) {
          tempoperators.push({
            key: docu.id,
            name: docu.data().name,
            approvedHandler: !!docu.data().approvedHandler,
          });
        }
      });
      setOperators(tempoperators);
    });

    const chemRef = collection(db, "Chemicals");
    const qc = query(chemRef, orderBy("chemicalName", "asc"), where("weedspray", "==", true));
    getDocs(qc).then((querySnapshot) => {
      const tempchems = [];
      querySnapshot.forEach((docu) => {
        tempchems.push({
          key: docu.id,
          chemicalName: docu.data().chemicalName,
          sprayRate: docu.data().qtyPerHectare,
          unit: docu.data().unit,
          weedspray: docu.data().weedspray,
          measure: "Hectare",
          ingredients: docu.data().ingredients,
          cost: docu.data().cost,
          nupointID: docu.data().nupointID || "",
        });
      });
      setChemicals(tempchems);
    });

    const tractorRef = collection(db, "Tractors");
    const qt = query(tractorRef, orderBy("sprayerName", "asc"), where("weedsprayer", "==", true));
    getDocs(qt).then((querySnapshot) => {
      const tempsprayers = [];
      querySnapshot.forEach((docu) => {
        tempsprayers.push({
          key: docu.id,
          sprayer: docu.data().sprayerName,
          tankCapacity: docu.data().tankCapacity,
        });
      });
      setSprayers(tempsprayers);
    });
  }, []);

  return (
    <MDBox py={12}>
      <Card sx={{ paddingTop: "40px", width: "80%", marginLeft: sidenavPadding }}>
        <Modal
          open={openModal}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <MDBox sx={modalstyle}>
            <MDTypography id="modal-modal-title" variant="h6" component="h2">
              <Icon>warning</Icon>Information Missing!
            </MDTypography>
            <MDTypography id="modal-modal-description" sx={{ mt: 2 }}>
              You are missing some vital information for this spray entry. Please check and try
              again. (Area, Chemical, Sprayer, Water Rate are all required)
            </MDTypography>
          </MDBox>
        </Modal>
        <Grid
          container
          justifyContent="center"
          xs={10}
          lg={10}
          mx="auto"
          textAlign="center"
          maxWidth="xl"
        >
          <Box sx={{ width: "100%" }}>
            <MDTypography variant="h3" mb={1}>
              Create New Weed Spray Record
            </MDTypography>

            {steps.map((step) => step.component)}
          </Box>
        </Grid>
        <Grid item xs={12} lg={7} sx={{ mx: "auto" }}>
          <MDBox width="100%" method="post" autocomplete="off">
            <MDBox p={3}>
              <Grid item justifyContent="center" xs={12} my={2}>
                {!loading && (
                  <MDButton variant="gradient" color="info" fullWidth onClick={() => saveSpray()}>
                    <Icon
                      sx={{
                        fontWeight: "bold",
                        color: ({ palette: { dark } }) => dark.main,
                        paddingRight: 2,
                      }}
                    >
                      add
                    </Icon>
                    Save Weed Spray
                  </MDButton>
                )}
                {loading && (
                  <MDButton disabled variant="gradient" color="warning" fullWidth>
                    <CircularProgress />
                  </MDButton>
                )}
              </Grid>
            </MDBox>
          </MDBox>
        </Grid>
      </Card>
    </MDBox>
  );
}

export default CreateWeedSpray;
